import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, ChevronDownIcon } from 'lucide-react';
import { ConditionRow } from './ConditionRow';
import { conditionRowGrid, daysPastEta, getEscalation, sortConditions } from '../../utils/conditions';
import type { ConditionPatch } from '../../hooks/useConditions';
import type { Condition, SortKey, SortState } from '../../types/conditions';
interface ConditionSectionProps {
  title: string;
  conditions: Condition[];
  emptyMessage: string;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: (ids: string[]) => void;
  onUpdate: (id: string, patch: ConditionPatch) => void;
  onComments: (id: string) => void;
  onUpload: (id: string) => void;
  onLinkDocuments: (id: string) => void;
  defaultOpen?: boolean;
  /** Show which group (Processor / Admin / Title) each row belongs to. */
  showCategory?: boolean;
  accent?: 'brand' | 'steel';
}
const columns: {
  label: string;
  key?: SortKey;
}[] = [{
  label: 'Condition',
  key: 'name'
}, {
  label: 'Status',
  key: 'status'
}, {
  label: 'Created',
  key: 'createdAt'
}, {
  label: 'Requested',
  key: 'requestedAt'
}, {
  label: 'ETA',
  key: 'eta'
}, {
  label: 'Assigned',
  key: 'assignee'
}, {
  label: 'Actions'
}];
export function ConditionSection({
  title,
  conditions,
  emptyMessage,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onUpdate,
  onComments,
  onUpload,
  onLinkDocuments,
  defaultOpen = true,
  showCategory = false,
  accent = 'brand'
}: ConditionSectionProps) {
  const needsAttention = conditions.filter(c => daysPastEta(c) > 0 || getEscalation(c) !== 'none').length;
  const [open, setOpen] = useState(defaultOpen);
  const [sort, setSort] = useState<SortState | null>(null);
  const sorted = sortConditions(conditions, sort);
  const ids = conditions.map(c => c.id);

  /** Click cycles: ascending → descending → original order. */
  function cycleSort(key: SortKey) {
    setSort(prev => {
      if (!prev || prev.key !== key) return {
        key,
        direction: 'asc'
      };
      if (prev.direction === 'asc') return {
        key,
        direction: 'desc'
      };
      return null;
    });
  }
  const allSelected = ids.length > 0 && ids.every(id => selectedIds.has(id));
  const someSelected = ids.some(id => selectedIds.has(id));
  const panelId = `section-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return <section className="border-b border-slate-200 last:border-b-0" data-id="element-127" data-mp="2z65j7phqm1dp">
      <h3 data-id="element-128" data-mp="337ijzl5z3edr">
        <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls={panelId} className={`flex w-full items-center gap-2.5 border-l-4 px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${accent === 'steel' ? 'border-steel bg-steel-light/70 hover:bg-steel-light' : 'border-brand bg-brand/[0.07] hover:bg-brand/[0.11]'}`} data-id="element-129" data-mp="336z2u6o5cu0s">
          <motion.span animate={{
          rotate: open ? 0 : -90
        }} transition={{
          duration: 0.15
        }} data-id="element-130" data-mp="1baqx6694qh8n">
            <ChevronDownIcon className={`h-5 w-5 ${accent === 'steel' ? 'text-steel' : 'text-brand'}`} aria-hidden="true" data-id="element-131" data-mp="16gcvjm3vjdvj" />
          </motion.span>
          <span className="text-base font-bold tracking-tight text-charcoal" data-id="element-132" data-mp="wlums2jlos4v">{title}</span>
          <span className={`min-w-[24px] rounded-full px-2 py-0.5 text-center text-xs font-bold tabular-nums text-white ${accent === 'steel' ? 'bg-steel' : 'bg-brand'}`} data-id="element-133" data-mp="1u70hvceje61m">
            {conditions.length}
          </span>
          {needsAttention > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-inset ring-red-200" data-id="element-134" data-mp="1fa9c8le9ry3k">
              <span className="h-1.5 w-1.5 rounded-full bg-red-600" aria-hidden="true" data-id="element-135" data-mp="3ub3yjqx1g3e" />
              {needsAttention} need{needsAttention === 1 ? 's' : ''} attention
            </span>}
        </button>
      </h3>

      <AnimatePresence initial={false} data-id="element-136" data-mp="1xhkwvkwp74hv">
        {open && <motion.div id={panelId} initial={{
        height: 0,
        opacity: 0,
        overflow: 'hidden'
      }} animate={{
        height: 'auto',
        opacity: 1,
        transitionEnd: {
          overflow: 'visible'
        }
      }} exit={{
        height: 0,
        opacity: 0,
        overflow: 'hidden'
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} data-id="element-137" data-mp="1is5b6jc0xp91">
            {conditions.length === 0 ? <p className="mx-5 mb-4 rounded-md border border-dashed border-slate-200 px-4 py-3 text-sm text-slate-500" data-id="element-138" data-mp="21id82jp4rjgs">
                {emptyMessage}
              </p> : <div className="pb-1" data-id="element-139" data-mp="q2yapet0epl5">
                <div role="table" aria-label={`${title} conditions`} className="w-full" data-id="element-140" data-mp="q1x0tdnqc950">
                  <div role="row" className={`${conditionRowGrid} border-y border-slate-200 bg-slate-50 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500`} data-id="element-141" data-mp="v2udxbtvuczn">
                    <div role="columnheader" data-id="element-142" data-mp="34ck5xx7d4mlz">
                      <input type="checkbox" checked={allSelected} ref={el => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }} onChange={() => onToggleSelectAll(ids)} aria-label={`Select all ${title.toLowerCase()} conditions`} className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" data-id="element-143" data-mp="3iaigerikbyw7" />
                    </div>
                    {columns.map(({
                label,
                key
              }) => {
                if (!key) {
                  return <div key={label} role="columnheader" className="text-right" data-id="element-144" data-mp="22u05j6rv9l2i">
                            {label}
                          </div>;
                }
                const active = sort?.key === key;
                const SortIcon = !active ? ArrowUpDownIcon : sort.direction === 'asc' ? ArrowUpIcon : ArrowDownIcon;
                return <div key={label} role="columnheader" aria-sort={active ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-145" data-mp="ki092fx6j8wh">
                          <button type="button" onClick={() => cycleSort(key)} className={`group -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 uppercase tracking-wide transition-colors hover:bg-slate-200/60 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${active ? 'text-brand' : ''}`} data-id="element-146" data-mp="3o0a8ln3fhthw">
                            {label}
                            <SortIcon className={`h-3 w-3 transition-opacity ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`} aria-hidden="true" data-id="element-147" data-mp="2sp3qwcqolkwh" />
                          </button>
                        </div>;
              })}
                  </div>
                  <AnimatePresence initial={false} data-id="element-148" data-mp="29ycj3fmce2k7">
                    {sorted.map(c => <ConditionRow key={c.id} condition={c} selected={selectedIds.has(c.id)} onToggleSelect={() => onToggleSelect(c.id)} onUpdate={patch => onUpdate(c.id, patch)} onComments={() => onComments(c.id)} onUpload={() => onUpload(c.id)} onLinkDocuments={() => onLinkDocuments(c.id)} showCategory={showCategory} data-id="element-149" data-mp="24zj6rqiskhlm" />)}
                  </AnimatePresence>
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </section>;
}